import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from '../i18n/index.js'
import type { CapturedPhoto } from '../lib/camera.js'
import { formatDate, formatDateTime, relativeDayLabel } from '../lib/format.js'
import { currentPosition } from '../lib/location.js'
import { enqueue } from '../lib/outbox.js'
import { PhotoSet } from './PhotoSlot.js'
import { SignaturePad, type Signature } from './SignaturePad.js'
import { WorkerShell, useWorkerTasks } from './shell.js'

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  // Opened from the tour list: go back there, not to the day view.
  const backTo = params.get('zurueck') === 'tour' ? '/app/tour' : '/app'
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { tasks, today, data, isLoading } = useWorkerTasks()

  const [before, setBefore] = useState<CapturedPhoto[]>([])
  const [after, setAfter] = useState<CapturedPhoto[]>([])
  const [other, setOther] = useState<CapturedPhoto[]>([])
  const [note, setNote] = useState('')
  const [defect, setDefect] = useState('')
  const [textOption, setTextOption] = useState<string | null>(null)
  const [measurement, setMeasurement] = useState('')
  const [signature, setSignature] = useState<Signature | null>(null)
  const [signedBy, setSignedBy] = useState('')
  const [saving, setSaving] = useState(false)

  const task = tasks.find((candidate) => candidate.id === id)

  if (!task) {
    return (
      <WorkerShell title={t('app.tasks')} back="/app">
        <p className="muted">{isLoading ? '…' : t('task.gone')}</p>
      </WorkerShell>
    )
  }

  const options = task.textOptions ?? []
  // Some Textbausteine, e.g. "kein Zugang", leave nothing to photograph.
  const photoFree = textOption !== null && (task.photoFreeOptions ?? []).includes(textOption)
  const needsBefore = !photoFree && task.photoRequirement === 'before_after'
  const needsAfter = !photoFree && task.photoRequirement !== 'none'
  const needsOption = options.length > 0
  const measurementLabel = task.templateMeasurementLabel ?? null
  // Phones offer a comma on the German keypad; accept both.
  const measurementValue = measurement.trim() === '' ? null : Number(measurement.trim().replace(',', '.'))
  const measurementOk = !measurementLabel || (measurementValue !== null && Number.isFinite(measurementValue))
  const signatureMode = task.signature ?? 'none'
  const signatureOk = signatureMode !== 'required' || signature !== null
  // Some Textbausteine, e.g. "kein Zugang", only help the office with a reason.
  const needsReason = textOption !== null && (task.reasonOptions ?? []).includes(textOption)
  const reasonOk = !needsReason || note.trim().length > 0
  const ready =
    reasonOk &&
    (!needsBefore || before.length > 0) &&
    (!needsAfter || after.length > 0) &&
    (!needsOption || textOption !== null) &&
    measurementOk &&
    signatureOk
  const address = [task.street, [task.zip, task.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')

  async function complete() {
    if (!task || saving) return
    setSaving(true)
    try {
      // Asked once, only here, and only when the office switched it on and
      // this person agreed. Never a trail through the day.
      const location = data?.gpsEnabled && data.shareLocation ? await currentPosition() : null
      await enqueue({
        id: crypto.randomUUID(),
        kind: 'completion',
        taskId: task.id,
        clientRef: crypto.randomUUID(),
        completedAtClient: new Date().toISOString(),
        note: note.trim() || undefined,
        textOption: textOption ?? undefined,
        measurementValue: measurementLabel && measurementValue !== null ? measurementValue : undefined,
        signedBy: signature ? signedBy.trim() || undefined : undefined,
        defectNote: task.legionella ? defect.trim() || undefined : undefined,
        location: location ?? undefined,
        photos: [
          ...before.map((p) => ({ kind: 'before' as const, blob: p.blob, takenAt: p.takenAt })),
          ...after.map((p) => ({ kind: 'after' as const, blob: p.blob, takenAt: p.takenAt })),
          ...other.map((p) => ({ kind: 'other' as const, blob: p.blob, takenAt: p.takenAt })),
          ...(signature ? [{ kind: 'signature' as const, blob: signature.blob, takenAt: signature.takenAt }] : []),
        ],
      })
      // The task list is refreshed rather than awaited: the outbox owns delivery
      // from here, so the worker is free to move on immediately.
      void queryClient.invalidateQueries({ queryKey: ['me-tasks'] })
      navigate(backTo, { replace: true })
    } finally {
      setSaving(false)
    }
  }

  if (task.status === 'done') {
    return (
      <WorkerShell title={task.title} back={backTo}>
        <div className="notice success" style={{ fontSize: 17 }}>
          {task.pendingUpload
            ? t('task.queued')
            : task.completedAtClient
              ? t('task.done_at', { date: formatDateTime(task.completedAtClient) })
              : t('task.saved')}
        </div>
        <div className="wk-detail-place">
          {task.siteName}, {task.label}
          {task.textOption && (
            <>
              <br />
              <strong>{task.textOption}</strong>
            </>
          )}
          {task.measurementValue !== null && task.measurementValue !== undefined && (
            <>
              <br />
              {task.measurementLabel}: <strong>{String(task.measurementValue).replace('.', ',')}{task.measurementUnit ? ` ${task.measurementUnit}` : ''}</strong>
            </>
          )}
        </div>
        <Link to={backTo} className="wk-btn" style={{ marginTop: 24 }}>
          {t('app.back')}
        </Link>
      </WorkerShell>
    )
  }

  return (
    <WorkerShell title={task.title} back={backTo}>
      {task.dueDate < today && (
        <div className="notice warn" style={{ fontSize: 17 }}>
          {t('task.was_due', { when: relativeDayLabel(task.dueDate, today) })}
        </div>
      )}
      {task.dueDate > today && (
        <div className="notice info" style={{ fontSize: 17 }}>
          {t('task.not_due_yet', { date: formatDate(task.dueDate) })}
        </div>
      )}

      <div className="wk-detail-place">
        <strong>{task.siteName}</strong>
        <br />
        {task.label}
        {task.externalRef && <span className="muted">, {task.externalRef}</span>}
        {task.unitLabel && (
          <>
            <br />
            <strong>{task.unitLabel}</strong>
          </>
        )}
        {address && (
          <>
            <br />
            <a
              href={`https://maps.apple.com/?q=${encodeURIComponent(address)}`}
              target="_blank"
              rel="noreferrer"
              className="small"
            >
              {address}
            </a>
          </>
        )}
      </div>

      {task.teamName && (
        <div className="wk-team">
          <strong>{t('card.team', { name: task.teamName })}</strong> {t('task.team_hint')}
        </div>
      )}
      {task.accessNote && (
        <div className="wk-access">
          <strong>{t('task.access')}:</strong> {task.accessNote}
        </div>
      )}

      {(task.description || task.note) && (
        <div className="wk-detail-desc">{task.note ?? task.description}</div>
      )}

      {needsOption && (
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, margin: '10px 0 6px' }}>{t('task.what_done')}</div>
          <div className="wk-options" role="radiogroup" aria-label={t('task.what_done')}>
            {options.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={textOption === option}
                className={`wk-option ${textOption === option ? 'on' : ''}`}
                onClick={() => setTextOption(option)}
              >
                <span className="dot" aria-hidden="true" />
                {option}
              </button>
            ))}
          </div>
        </div>
      )}

      {measurementLabel && (
        <label className="field" style={{ marginTop: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {measurementLabel}
            {task.templateMeasurementUnit ? ` (${task.templateMeasurementUnit})` : ''}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={measurement}
            onChange={(event) => setMeasurement(event.target.value)}
            placeholder={t('task.measurement_placeholder')}
            style={{ fontSize: 20, minHeight: 56 }}
          />
        </label>
      )}

      <PhotoSet label={t('task.photo_before')} photos={before} onChange={setBefore} required={needsBefore} />
      <PhotoSet label={t('task.photo_after')} photos={after} onChange={setAfter} required={needsAfter} />
      <PhotoSet label={t('task.photo_other')} photos={other} onChange={setOther} required={false} />

      {signatureMode !== 'none' && (
        <>
          <SignaturePad
            label={signatureMode === 'required' ? t('task.signature') : t('task.signature_optional')}
            signature={signature}
            onChange={setSignature}
          />
          {signature && (
            <label className="field">
              <span style={{ fontSize: 16 }}>{t('task.signed_by')}</span>
              <input value={signedBy} onChange={(event) => setSignedBy(event.target.value)} maxLength={120} style={{ fontSize: 17 }} />
            </label>
          )}
        </>
      )}

      <label className="field" style={{ marginTop: 8 }}>
        <span style={{ fontSize: 16, fontWeight: needsReason ? 700 : undefined }}>{needsReason ? t('task.reason_label') : t('task.note')}</span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={needsReason ? t('task.reason_placeholder') : t('task.note_placeholder')}
          style={{ fontSize: 17, minHeight: 90 }}
        />
      </label>

      {task.legionella && (
        <label className="field" style={{ marginTop: 8 }}>
          <span style={{ fontSize: 16 }}>{t('task.defect_label')}</span>
          <textarea
            value={defect}
            onChange={(event) => setDefect(event.target.value)}
            maxLength={1000}
            placeholder={t('task.defect_placeholder')}
            style={{ fontSize: 17, minHeight: 70 }}
          />
        </label>
      )}

      {!ready && (
        <p className="muted" style={{ fontSize: 16 }}>
          {needsOption && textOption === null
            ? t('task.option_required')
            : !reasonOk
              ? t('task.reason_required')
              : !measurementOk
              ? t('task.measurement_required')
              : !signatureOk
                ? t('task.signature_required')
                : needsBefore
                  ? t('task.photo_required_both')
                  : t('task.photo_required_after')}
        </p>
      )}

      <button type="button" className="wk-btn" disabled={!ready || saving} onClick={() => void complete()}>
        {saving ? t('task.completing') : `✓ ${t('task.complete')}`}
      </button>

      <Link to={`/app/melden?objectId=${task.objectId}&taskId=${task.id}`} className="wk-btn ghost" style={{ marginTop: 12 }}>
        {t('task.report_issue')}
      </Link>
    </WorkerShell>
  )
}
